import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtQty, fmtDT, fmtINR } from "@/lib/api";
import { PageHeader, Empty, Field, Combobox, StatusBadge, tableWrap, th, td, num } from "@/components/common";

function RequestDialog({ onClose, onDone }) {
  const [drums, setDrums] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [f, setF] = useState({ drum_id: "", direction: "decrease", qty: "", reason: "Damaged", remarks: "" });
  useEffect(() => {
    api.get("/drums", { params: { kind: "drum,lot,offcut" } }).then((r) => setDrums(r.data.filter((d) => ["full", "partial"].includes(d.status))));
    api.get("/adjustments/reasons").then((r) => setReasons(r.data));
  }, []);
  const sel = drums.find((d) => d.id === f.drum_id);
  const save = async () => {
    try { await api.post("/adjustments", { ...f, qty: Number(f.qty) }); toast.success("Sent to Super Admin for approval"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="adjust-dialog">
        <DialogHeader><DialogTitle>Request a stock adjustment</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Drum / lot / offcut" required>
            <Combobox testid="adjust-drum-select" value={f.drum_id} onChange={(v) => setF({ ...f, drum_id: v })}
              options={drums.map((d) => ({ value: d.id, label: `${d.drum_code} · ${d.brand} ${d.cable_size} · ${fmtQty(d.current_balance, d.uom)}` }))} />
          </Field>
          {sel && <div className="text-xs text-muted-foreground" data-testid="adjust-drum-info">Balance {fmtQty(sel.current_balance, sel.uom)} · reserved {fmtQty(sel.reserved, sel.uom)}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Direction" required>
              <select className="h-10 rounded-md border bg-background px-3 text-sm" value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} data-testid="adjust-direction-select">
                <option value="decrease">Decrease (loss)</option><option value="increase">Increase (gain)</option>
              </select>
            </Field>
            <Field label="Quantity" required><Input type="number" min="0" step="0.01" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="adjust-qty-input" /></Field>
          </div>
          <Field label="Reason" required>
            <select className="h-10 rounded-md border bg-background px-3 text-sm" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="adjust-reason-select">
              {reasons.filter((r) => r !== "Physical count variance").map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Explanation" required><Input value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="adjust-remarks-input" /></Field>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.drum_id || !f.qty} data-testid="adjust-submit-btn">Submit for approval</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Adjustments() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get("/adjustments", { params: { status } }).then((r) => setRows(r.data)); }, [status]);
  useEffect(() => { load(); }, [load]);
  const act = async (a, path) => {
    let body;
    if (path === "reject") { const reason = window.prompt("Reason for rejecting"); if (!reason) return; body = { reason }; }
    else if (!window.confirm(`Approve ${a.adj_no}: ${a.direction} ${a.qty} ${a.uom}?`)) return;
    try { await api.post(`/adjustments/${a.id}/${path}`, body); toast.success(path === "approve" ? "Posted to stock ledger" : "Rejected"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Stock Adjustments" subtitle="Every stock correction needs a reason and Super Admin approval before it changes stock.">
        {can("adjust:request") && <Button onClick={() => setOpen(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="new-adjust-btn"><Plus className="h-4 w-4 mr-1" />Request Adjustment</Button>}
      </PageHeader>
      <div className="flex gap-2 mb-4 flex-wrap">
        {[["", "All"], ["pending", "Pending"], ["posted", "Posted"], ["rejected", "Rejected"]].map(([v, l]) => (
          <Button key={v} size="sm" variant={status === v ? "default" : "outline"} onClick={() => setStatus(v)} data-testid={`adjust-filter-${v || "all"}`}>{l}</Button>
        ))}
      </div>
      <div className={tableWrap}>
        <table className="w-full" data-testid="adjustments-table">
          <thead><tr>{["Adj No", "Date", "Source", "Brand", "Cable Size", "Drum", "Change", ...(can("cost:view") ? ["Value"] : []), "Reason", "Requested By", "Status", "Approved / Rejected", ""].map((h, i) => <th key={i} className={th}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((a) => (
            <tr key={a.id} className="hover:bg-muted/40" data-testid={`adjust-row-${a.adj_no}`}>
              <td className={`${td} font-mono`}>{a.adj_no}</td><td className={td}>{a.date}</td>
              <td className={td}>{a.source === "count" ? <Link className="hover:underline text-primary" to={`/reconciliation/${a.count_id}`}>{a.count_no}</Link> : a.source === "direct_edit" ? "Direct edit" : "Manual"}</td>
              <td className={td}>{a.brand}</td><td className={td}>{a.cable_size}</td>
              <td className={`${td} font-mono text-xs`}>{a.drum_code || (a.applied || []).map((x) => x.drum_code).join(", ")}</td>
              <td className={`${td} ${num} font-semibold ${a.direction === "increase" ? "text-emerald-600" : "text-red-600"}`}>{a.direction === "increase" ? "+" : "−"}{fmtQty(a.qty, a.uom)}</td>
              {can("cost:view") && <td className={`${td} ${num}`}>{fmtINR(a.value)}</td>}
              <td className={`${td} max-w-[260px] truncate`} title={`${a.reason} · ${a.remarks}`}>{a.reason} · {a.remarks}</td>
              <td className={td}>{a.requested_by?.name}<div className="text-[11px] text-muted-foreground">{fmtDT(a.requested_at)}</div></td>
              <td className={td}><StatusBadge status={a.status} /></td>
              <td className={td}>{a.approved_by?.name || "—"}{a.reject_reason && <div className="text-[11px] text-red-600">{a.reject_reason}</div>}</td>
              <td className={td}>{a.status === "pending" && can("adjust:approve") && <div className="flex gap-1">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(a, "approve")} data-testid={`adjust-approve-${a.adj_no}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => act(a, "reject")} data-testid={`adjust-reject-${a.adj_no}`}>Reject</Button>
              </div>}</td>
            </tr>
          ))}</tbody>
        </table>
        {!rows.length && <Empty text="No adjustments" />}
      </div>
      {open && <RequestDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}
